import React, { forwardRef, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPopper } from "@popperjs/core";
import Input from "../../components/Elements/Input.js";

const Select = forwardRef(({
  border,
  size,
  leftIcon,
  rightIcon,
  items,
  closeOnSelect,
  placeholder,
  defaultValue,
  resetKey,
  onChange, // Make sure this prop is passed
  ...rest
}, ref) => {
  const [inputValue, setInputValue] = React.useState(defaultValue);
  const [menuClasses, setMenuClasses] = React.useState("");
  const [menuShow, setMenuShow] = React.useState(false);
  const [animating, setAnimating] = React.useState(false);
  const [transformOrigin, setTransformOrigin] = React.useState(
    "origin-top-right"
  );
  const btnDropdownRef = useRef();
  const popoverDropdownRef = useRef();
  const origins = {
    "top-start": "origin-bottom-left",
    "top-end": "origin-bottom-right",
    "bottom-start": "origin-top-left",
    "bottom-end": "origin-top-right",
    "right-start": "origin-top-left",
    "right-end": "origin-top-left",
    "left-start": "origin-top-right",
    "left-end": "origin-top-right",
    bottom: "origin-top",
    top: "origin-bottom",
    left: "origin-right",
    right: "origin-left",
  };

  const handleClickOutside = (event) => {
    if (popoverDropdownRef.current && !popoverDropdownRef.current.contains(event.target) &&
        btnDropdownRef.current && !btnDropdownRef.current.contains(event.target)) {
      setMenuShow(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const startAnimation = () => {
    if (!animating) {
      setAnimating(true);
      if (menuShow) {
        setMenuClasses("");
        setTimeout(function () {
          setMenuShow(false);
        }, 310);
      } else {
        let popperObject = createPopper(
          btnDropdownRef.current,
          popoverDropdownRef.current,
          {
            placement: "bottom-start",
          }
        );
        setTransformOrigin(origins[popperObject.state.placement]);
        setMenuShow(true);
        setTimeout(function () {
          setMenuClasses("opacity-100 scale-100 ");
        }, 10);
      }
      setTimeout(function () {
        setAnimating(false);
      }, 350);
    }
  };

  const handleSelect = (value) => {
    setInputValue(value);
    if (onChange) {
      onChange(value);
    }
    if (closeOnSelect) {
      startAnimation();
    }
  };

  // This use effect is to listen to the reset flag 
  // to know when to clear states
  useEffect(() => {
    setInputValue(defaultValue);  // Reset the input value when form submits
  }, [resetKey, defaultValue]);

  return (
    <>
      <div className="relative">
        <Input
          value={inputValue}
          type="text"
          onChange={() => {}}
          onClick={startAnimation}
          placeholder={placeholder}
          ref={ref || btnDropdownRef}
          size={size}
        />
        <div
          ref={popoverDropdownRef}
          className={(menuShow ? "block " : "hidden ") + "z-50"}
        >
          <div
            className={
              menuClasses +
              " absolute opacity-0 border bg-white text-base z-50 float-left p-2 text-left rounded-lg shadow-lg min-w-48 transition-all duration-200 ease-in-out transform scale-95 origin-top-left " +
              transformOrigin
            }
          >
            {items.map((prop, key) => {
              if (prop.disabled) {
                return (
                  <span
                    className="text-sm pt-2 pb-0 px-4 font-bold block w-full whitespace-nowrap bg-transparent text-blueGray-500"
                    key={key}
                  >
                    {prop.text}
                  </span>
                );
              } else {
                return (
                  <a
                    href="#pablo"
                    key={key}
                    className={
                      (prop === inputValue ? "bg-lightBlue-100 " : "") +
                      "text-sm px-3 py-2 font-normal block w-full whitespace-nowrap transition-all duration-200 hover:bg-blueGray-100 rounded"
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      handleSelect(prop);
                    }}
                  >
                    {prop}
                  </a>
                );
              }
            })}
          </div>
        </div>
      </div>
    </>
  );
});

Select.defaultProps = {
  border: "border",
  size: "regular",
  items: [],
  closeOnSelect: true,
  defaultValue: "",
};

Select.propTypes = {
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  border: PropTypes.oneOf(["border", "borderless"]),
  closeOnSelect: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        text: PropTypes.string,
      }),
      PropTypes.string,
    ])
  ),
  size: PropTypes.oneOf(["sm", "lg", "regular"]),
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  resetKey: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Select;
